import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { motion } from "framer-motion";

import { useCurrentPermissions } from "../../contexts/PermissionsProvider";
import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import {
  ActionRow,
  EmptyState,
  InlineFilter,
  PageHeader,
  ScopeBadge,
  ScopeSelector,
  SectionCard,
  SkeletonTable,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import {
  createGroup,
  deleteGroup,
  getAccounts,
  getGroup,
  getGroups,
  removeGroupMember,
  revokeRoleAssignment,
  updateGroup,
} from "./api";

/**
 * DirectoryGroups — editable Groups list.
 *
 * "New group" CTA + row kebab (Rename / Delete / View) + row-click
 * drawer with Members + Roles sections.
 *
 * SYSTEM: cross-tenant list; writes hit /api/groups (coord infers
 *   the caller has no accountScope from the bearer token).
 * ACCOUNT_ADMIN: coord narrows the list to the caller's own account;
 *   writes hit /api/accounts/{aid}/groups. Row-level scope is
 *   inferred from `accountId` so the same UI serves both bands.
 */

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function DirectoryGroups() {
  const { permissions } = useCurrentPermissions();
  // Cross-tenant view is gated on the same entitlement as the Accounts
  // tab — `directory.account.manage`. SYSTEM remains the wildcard so
  // platform super-users always land on the cross-tenant table. Post
  // PR-1.5f we no longer gate on role-name strings; the old
  // SYSTEM_DIRECTORY_ADMIN name is renamed to DIRECTORY_ADMIN in V88.
  const isSystem =
    (permissions || []).includes("SYSTEM") ||
    (permissions || []).includes("directory.account.manage");

  const [groups, setGroups] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [toast, setToast] = useState(null);

  // SYSTEM-only scope selector state, persisted in URL:
  //   ?scope=all|system|account  &  ?accountId=<uuid>
  // Non-SYSTEM callers still get the params from URL but the selector
  // is hidden — the effective filter defaults to "all" (which coord
  // then narrows to their own account anyway).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawScope = searchParams.get("scope");
  const scope =
    rawScope === "system" || rawScope === "account" ? rawScope : "all";
  const accountId = searchParams.get("accountId") || null;

  // Account picker options — only fetched when SYSTEM to keep the
  // ACCOUNT_ADMIN path zero-cost.
  const [accounts, setAccounts] = useState(null);
  useEffect(() => {
    if (!isSystem) return;
    let cancelled = false;
    getAccounts()
      .then((data) => {
        if (!cancelled) setAccounts(data || []);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isSystem]);

  // New-group dialog state.
  const [newOpen, setNewOpen] = useState(false);

  // Row-scoped dialogs.
  const [renaming, setRenaming] = useState(null); // group row
  const [deleting, setDeleting] = useState(null); // group row
  const [detailOpen, setDetailOpen] = useState(null); // group row
  // Add-members picker fired directly from the row kebab (bypasses
  // the detail drawer so ops can bulk-add without a two-step open).
  const [addMembersRow, setAddMembersRow] = useState(null);

  // Kebab menu.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Turn the ScopeSelector value into the coord query payload —
  // "system" pins the SYSTEM bucket, "account" pins one account,
  // "all" clears both narrows.
  const scopePayload = useMemo(() => {
    if (scope === "system") return { scope: "SYSTEM" };
    if (scope === "account" && accountId)
      return { scope: "ACCOUNT", accountId };
    return {};
  }, [scope, accountId]);

  const load = useCallback(() => {
    setErr(null);
    setGroups(null);
    getGroups(scopePayload)
      .then(setGroups)
      .catch((e) =>
        setErr(
          e.status === 403
            ? "You don't have permission to view groups on this account."
            : `Failed to load groups: ${e.message}`
        )
      );
  }, [scopePayload]);

  useEffect(() => {
    load();
  }, [load]);

  const handleScopeChange = ({ scope: nextScope, accountId: nextAccountId }) => {
    const next = new URLSearchParams(searchParams);
    if (nextScope && nextScope !== "all") next.set("scope", nextScope);
    else next.delete("scope");
    if (nextAccountId) next.set("accountId", nextAccountId);
    else next.delete("accountId");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(
    () =>
      groups?.filter((g) => {
        if (!q) return true;
        const n = q.toLowerCase();
        return (
          (g.name ?? "").toLowerCase().includes(n) ||
          (g.id ?? "").toLowerCase().includes(n)
        );
      }) ?? [],
    [groups, q]
  );

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const columnSpec = [
    { label: "Name" },
    ...(isSystem ? [{ label: "Account" }] : []),
    { label: "Members", align: "right", width: 96 },
    { label: "Roles", align: "right", width: 80 },
    { label: "Created", width: 140 },
    { label: "ID", mono: true },
    { label: "", align: "right", width: 48 },
  ];

  const newGroupButton = (
    <Tooltip
      title="Create a group. Add users, then bind roles to the group instead of assigning per-user."
      arrow
      enterDelay={400}
    >
      <span>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setNewOpen(true)}
          sx={{ textTransform: "none" }}
          aria-label="New group"
        >
          New group
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <PageHeader
        title="Groups"
        titleInfo="A group bundles users so a role assigned to the group applies to every member — no per-user grants to maintain."
        subtitle={
          "Groups let you assign a role to many users at once. " +
          (isSystem ? "Cross-tenant list." : "Scoped to your account.")
        }
        action={newGroupButton}
      />

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <ScopeSelector
        variant="groups"
        value={{ scope, accountId }}
        onChange={handleScopeChange}
        accounts={accounts}
        hidden={!isSystem}
        sx={{ mb: 2 }}
      />

      {!groups && !err && <SkeletonTable columns={columnSpec} rows={6} />}

      {groups && (
        <>
          <InlineFilter
            value={q}
            onChange={setQ}
            placeholder="Filter by name or id"
            count={{
              shown: filtered.length,
              total: groups.length,
              noun: "group",
            }}
          />

          {filtered.length === 0 ? (
            <EmptyState
              icon={<WorkspacesOutlinedIcon />}
              title={
                q || scope !== "all" ? "No groups match" : "No groups yet"
              }
              description={
                q || scope !== "all"
                  ? "Try clearing the filter or widening the scope."
                  : "Create a group to bundle members and assign a role to all of them at once."
              }
              variant={q || scope !== "all" ? "no-match" : "empty"}
              primaryAction={q || scope !== "all" ? undefined : newGroupButton}
            />
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: dirTokens.radius }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columnSpec.map((c, i) => (
                      <TableCell
                        key={c.label || `col-${i}`}
                        align={c.align || "left"}
                        sx={c.mono ? { fontFamily: "monospace" } : undefined}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((g) => (
                    <ActionRow
                      key={g.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setDetailOpen(g)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {g.name || "—"}
                        </Typography>
                      </TableCell>
                      {isSystem && (
                        <TableCell>
                          {g.accountId ? (
                            <ScopeBadge
                              scope="account"
                              name={g.accountName || g.accountId}
                            />
                          ) : (
                            <ScopeBadge scope="system" />
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <span
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 13,
                          }}
                        >
                          {typeof g.memberCount === "number"
                            ? g.memberCount
                            : Array.isArray(g.members)
                              ? g.members.length
                              : "—"}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 13,
                          }}
                        >
                          {typeof g.roleCount === "number"
                            ? g.roleCount
                            : Array.isArray(g.roles)
                              ? g.roles.length
                              : "—"}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(g.createdOn)}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        {g.id}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 48 }}>
                        <Box className="row-actions" sx={{ display: "inline-flex" }}>
                          <Tooltip title="More actions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMenu(e, g);
                              }}
                              aria-label="Group actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </ActionRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <Tooltip
          title="Add users to this group. They inherit every role held by the group."
          arrow
          enterDelay={400}
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setAddMembersRow(menuRow);
              closeMenu();
            }}
          >
            Add members…
          </MenuItem>
        </Tooltip>
        <Tooltip
          title="Change the group's name or description. Members and role bindings stay put."
          arrow
          enterDelay={400}
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setRenaming(menuRow);
              closeMenu();
            }}
          >
            Rename…
          </MenuItem>
        </Tooltip>
        <Tooltip
          title="Open the members + roles drawer for this group."
          arrow
          enterDelay={400}
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setDetailOpen(menuRow);
              closeMenu();
            }}
          >
            View members
          </MenuItem>
        </Tooltip>
        <Tooltip
          title="Delete the group. Members lose the role bundle immediately; the users themselves aren't deleted."
          arrow
          enterDelay={400}
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setDeleting(menuRow);
              closeMenu();
            }}
            sx={{ color: "error.main" }}
          >
            Delete…
          </MenuItem>
        </Tooltip>
      </Menu>

      <GroupCreateDialog
        open={newOpen}
        scopeAccountId={null /* SYSTEM writes; ACCOUNT_ADMIN callers
          will get the account scope inferred by coord from the
          bearer token — the PR-1.5b endpoint tolerates POST /api/groups
          from ACCOUNT_ADMIN and routes to their account. */}
        onClose={() => setNewOpen(false)}
        onCreated={(g) => {
          setNewOpen(false);
          setToast({ message: `Group "${g.name}" created.` });
          load();
        }}
      />

      {renaming && (
        <GroupRenameDialog
          group={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            setToast({ message: "Group updated." });
            load();
          }}
        />
      )}

      {deleting && (
        <GroupDeleteDialog
          group={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setToast({ message: "Group deleted." });
            load();
          }}
        />
      )}

      <GroupDetailDrawer
        group={detailOpen}
        onClose={() => setDetailOpen(null)}
        onChanged={load}
      />

      {/* Row-kebab shortcut — opens the users picker directly for the
          selected group, bypassing the detail drawer. */}
      {addMembersRow && (
        <AssignmentPickerDialog
          open
          mode="add_users_to_group"
          groupId={addMembersRow.id}
          scopeAccountId={addMembersRow.accountId || null}
          onClose={() => setAddMembersRow(null)}
          onSaved={({ count }) => {
            setAddMembersRow(null);
            setToast({
              message: `Added ${count} member${count === 1 ? "" : "s"}.`,
            });
            load();
          }}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity="success"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </motion.div>
  );
}

// ---------------- subcomponents ----------------

export function GroupCreateDialog({ open, scopeAccountId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setError(null);
    setBusy(false);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const g = await createGroup({ scopeAccountId, name, description });
      onCreated && onCreated(g || { name });
    } catch (e) {
      setError(e.message || "Create failed. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New group</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && (
            <Typography sx={{ color: "error.main" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
        >
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GroupRenameDialog({ group, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateGroup({
        scopeAccountId: group.accountId || null,
        gid: group.id,
        name,
        description,
      });
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || "Update failed. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename group</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && (
            <Typography sx={{ color: "error.main" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GroupDeleteDialog({ group, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteGroup({
        scopeAccountId: group.accountId || null,
        gid: group.id,
      });
      onDeleted && onDeleted();
    } catch (e) {
      setError(e.message || "Delete failed. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete {group.name}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          Members lose the role bundle this group carries immediately.
          The group is unrecoverable — the users themselves aren&apos;t
          deleted.
        </Typography>
        {error && (
          <Typography sx={{ color: "error.main", mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={submit}
          disabled={busy}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GroupDetailDrawer({ group, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = useState(false);

  const refresh = useCallback(() => {
    if (!group) return;
    setLoadErr(null);
    setDetail(null);
    getGroup(group.id)
      .then(setDetail)
      .catch((e) =>
        setLoadErr(
          e.status === 403
            ? "You don't have permission to view this group."
            : `Failed to load group: ${e.message}`
        )
      );
  }, [group]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const scopeAccountId = detail?.accountId || group?.accountId || null;

  const removeMember = async (userId) => {
    try {
      await removeGroupMember({
        scopeAccountId,
        gid: group.id,
        userId,
      });
      refresh();
      onChanged && onChanged();
    } catch (e) {
      setLoadErr(
        `Failed to remove member. HTTP ${e.status || "?"} — try again in a moment.`
      );
    }
  };

  const revokeRole = async (roleId) => {
    try {
      if (scopeAccountId) {
        await revokeRoleAssignment({
          accountId: scopeAccountId,
          subjectType: "GROUP",
          subjectId: group.id,
          roleId,
        });
      } else {
        await revokeRoleAssignment({ groupId: group.id, roleId });
      }
      refresh();
      onChanged && onChanged();
    } catch (e) {
      setLoadErr(
        `Failed to revoke role. HTTP ${e.status || "?"} — try again in a moment.`
      );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={!!group}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 460 } } }}
    >
      {group && (
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6" fontWeight={700}>
              {group.name}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            {group.accountId ? (
              <ScopeBadge
                scope="account"
                name={group.accountName || group.accountId}
              />
            ) : (
              <ScopeBadge scope="system" />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {group.id}
            </Typography>
          </Stack>

          {loadErr && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loadErr}
            </Alert>
          )}

          {!detail && !loadErr && (
            <Stack spacing={2}>
              <SectionCard title="Members" isEmpty emptyState={{ description: "Loading…" }} />
              <SectionCard title="Roles" isEmpty emptyState={{ description: "Loading…" }} />
            </Stack>
          )}

          {detail && (
            <Stack spacing={2}>
              <SectionCard
                title="Members"
                count={(detail.members || []).length}
                action={
                  <Tooltip
                    title="Add users to this group. They inherit every role held by the group."
                    arrow
                    enterDelay={400}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PersonAddAlt1OutlinedIcon />}
                      onClick={() => setAddMemberOpen(true)}
                      sx={{ textTransform: "none" }}
                      aria-label="Add members"
                    >
                      Add members
                    </Button>
                  </Tooltip>
                }
                isEmpty={!(detail.members || []).length}
                emptyState={{
                  title: "No members yet",
                  description: "Add members so the group's role bundle applies to real users.",
                }}
              >
                <MembersList
                  members={detail.members || []}
                  onRemove={removeMember}
                />
              </SectionCard>

              <SectionCard
                title="Roles"
                count={(detail.roles || []).length}
                action={
                  <Tooltip
                    title="Grant this group a role — every current and future member gets it."
                    arrow
                    enterDelay={400}
                  >
                    <Button
                      size="small"
                      startIcon={<ShieldOutlinedIcon />}
                      onClick={() => setAssignRoleOpen(true)}
                      sx={{ textTransform: "none" }}
                      aria-label="Assign role"
                    >
                      Assign role
                    </Button>
                  </Tooltip>
                }
                isEmpty={!(detail.roles || []).length}
                emptyState={{
                  title: "No roles assigned",
                  description: "Assign a role to grant its entitlements to every member of this group.",
                }}
              >
                <RolesList
                  roles={detail.roles || []}
                  onRevoke={revokeRole}
                />
              </SectionCard>
            </Stack>
          )}
        </Box>
      )}

      {group && (
        <>
          <AssignmentPickerDialog
            open={addMemberOpen}
            mode="add_users_to_group"
            groupId={group.id}
            scopeAccountId={scopeAccountId}
            onClose={() => setAddMemberOpen(false)}
            onSaved={() => {
              setAddMemberOpen(false);
              refresh();
              onChanged && onChanged();
            }}
          />
          <AssignmentPickerDialog
            open={assignRoleOpen}
            mode="assign_role_to_group"
            groupId={group.id}
            scopeAccountId={scopeAccountId}
            onClose={() => setAssignRoleOpen(false)}
            onSaved={() => {
              setAssignRoleOpen(false);
              refresh();
              onChanged && onChanged();
            }}
          />
        </>
      )}
    </Drawer>
  );
}

function MembersList({ members, onRemove }) {
  return (
    <Stack spacing={0.5}>
      {members.map((m) => (
        <Stack
          key={m.id || m.userId}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            py: 0.75,
            px: 1,
            borderRadius: 1,
            "& .row-actions": {
              opacity: 0,
              transition: "opacity 150ms ease-in-out",
            },
            "&:hover, &:focus-within": {
              bgcolor: "action.hover",
              "& .row-actions": { opacity: 1 },
            },
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {m.name || m.email || m.id || m.userId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {m.email || (m.id || m.userId)}
            </Typography>
          </Box>
          <Box className="row-actions">
            <Tooltip title="Remove from group">
              <IconButton
                size="small"
                color="error"
                onClick={() => onRemove(m.id || m.userId)}
                aria-label={`Remove ${m.name || m.email || "member"} from group`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function RolesList({ roles, onRevoke }) {
  return (
    <Stack spacing={0.5}>
      {roles.map((r) => (
        <Stack
          key={r.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            py: 0.75,
            px: 1,
            borderRadius: 1,
            "& .row-actions": {
              opacity: 0,
              transition: "opacity 150ms ease-in-out",
            },
            "&:hover, &:focus-within": {
              bgcolor: "action.hover",
              "& .row-actions": { opacity: 1 },
            },
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {r.name || r.id}
            </Typography>
            {r.description && (
              <Typography variant="caption" color="text.secondary">
                {r.description}
              </Typography>
            )}
          </Box>
          <Box className="row-actions">
            <Tooltip title="Revoke role">
              <IconButton
                size="small"
                color="error"
                onClick={() => onRevoke(r.id)}
                aria-label={`Revoke ${r.name || "role"}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// Note: the previous local AddMembersToGroupDialog was retired here.
// The shared AssignmentPickerDialog now carries an `add_users_to_group`
// mode (Mode D) that does exactly what this used to do, so the drawer's
// "Add members" CTA and the row-kebab shortcut both route through the
// same component. Keeping one picker means one bug-fix surface and one
// SOC 2 error-toast contract (never echo other tenants' names/ids).
